import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, RESOURCE_TYPES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Sparkles, Globe, Youtube, Instagram } from "lucide-react";

const TYPE_ICONS = {
  web_page: Globe,
  youtube_video: Youtube,
  x_post: () => (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  instagram_reel: Instagram,
};

export default function IngestPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [type, setType] = useState("web_page");
  const [submitting, setSubmitting] = useState(false);

  const detectType = (u) => {
    const v = u.toLowerCase();
    if (v.includes("youtube.com") || v.includes("youtu.be")) return "youtube_video";
    if (v.includes("twitter.com") || v.includes("x.com")) return "x_post";
    if (v.includes("instagram.com")) return "instagram_reel";
    return "web_page";
  };

  const handleUrlChange = (e) => {
    const val = e.target.value;
    setUrl(val);
    if (val && val.startsWith("http")) {
      setType(detectType(val));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSubmitting(true);
    try {
      const created = await api.ingest(url.trim(), type);
      toast.success("Distillazione avviata", {
        description: "Ti stiamo portando alla scheda…",
      });
      navigate(`/r/${created.id}`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Errore sconosciuto durante l'ingest";
      toast.error("Ingest fallito", { description: String(msg) });
      setSubmitting(false);
    }
  };

  return (
    <section
      className="max-w-5xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20"
      data-testid="ingest-page"
    >
      <div className="flex flex-col items-start gap-6 mb-12">
        <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 inline-flex items-center gap-2">
          <Sparkles size={14} /> Distillatore di risorse
        </span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-balance max-w-4xl">
          Trasforma qualsiasi link in una scheda leggibile in{" "}
          <span style={{ color: "#002FA7" }}>pochi secondi</span>.
        </h1>
        <p className="text-lg text-zinc-600 max-w-2xl leading-relaxed">
          Incolla l'URL di una pagina web, un video YouTube, un post su X o un
          Reel Instagram. ResourceDistiller estrae il contenuto e lo distilla in
          titolo, panoramica, punti chiave, step e riferimenti.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-white border border-zinc-200 rounded-2xl p-6 md:p-8 shadow-sm flex flex-col gap-5"
      >
        <div>
          <label
            htmlFor="ingest-url"
            className="block text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2"
          >
            URL della risorsa
          </label>
          <input
            id="ingest-url"
            data-testid="ingest-url-input"
            type="url"
            required
            value={url}
            onChange={handleUrlChange}
            placeholder="https://…"
            className="w-full text-xl md:text-2xl py-4 md:py-5 px-5 rounded-xl border-2 border-zinc-200 focus:border-[#002FA7] focus:ring-4 focus:ring-[#002FA7]/10 outline-none transition-all font-mono"
          />
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:items-end">
          <div className="flex-1">
            <label className="block text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2">
              Tipo di risorsa
            </label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger
                data-testid="ingest-type-select"
                className="h-12 text-base bg-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOURCE_TYPES.map((t) => {
                  const Icon = TYPE_ICONS[t.value];
                  return (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="inline-flex items-center gap-2">
                        <Icon size={14} /> {t.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            disabled={submitting || !url.trim()}
            data-testid="ingest-submit-button"
            className="h-12 px-6 text-base text-white hover:opacity-90 transition-all"
            style={{ backgroundColor: "#002FA7" }}
          >
            {submitting ? "Avvio…" : "Distilla"}
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>

        {(type === "x_post" || type === "instagram_reel") && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 leading-relaxed">
            <strong>Nota sui ToS:</strong> l'estrazione di{" "}
            {type === "x_post" ? "post X" : "Reel Instagram"} senza API ufficiali
            può essere limitata o soggetta ai rispettivi Termini di Servizio. In
            caso di errore, puoi modificare manualmente la scheda.
          </div>
        )}
      </form>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            n: "01",
            title: "Estrai",
            body: "Raschiamo articoli con Readability/Trafilatura, trascrizioni YouTube, metadati social.",
          },
          {
            n: "02",
            title: "Distilla",
            body: "Un modello LLM strutturato produce titolo, sintesi, keyword, punti chiave e step.",
          },
          {
            n: "03",
            title: "Consulta",
            body: "La tua libreria filtrabile per keyword, con ricerca full-text e vista dettaglio.",
          },
        ].map((s) => (
          <div
            key={s.n}
            className="border border-zinc-200 rounded-xl p-6 hover:border-zinc-300 transition-colors"
          >
            <span className="font-mono text-xs text-zinc-400">{s.n}</span>
            <h3 className="font-display text-xl font-medium mt-3 mb-2">
              {s.title}
            </h3>
            <p className="text-sm text-zinc-600 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
